import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const CompletionStep = ({ data, onComplete, onBack, loading }) => {
  const getUserTypeLabel = (type) => {
    const labels = {
      parent: 'Parent/Caregiver',
      elderly: 'Elderly Patient',
      chronic: 'Chronic Condition',
      general: 'General Adult'
    };
    return labels?.[type] || 'General Adult';
  };

  return (
    <div className="space-y-8 py-8">
      <div className="text-center">
        <div className="inline-flex items-center justify-center w-20 h-20 bg-success/10 rounded-full mb-4">
          <Icon name="CheckCircle2" size={48} className="text-success" />
        </div>
        <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-3">
          You're All Set!
        </h2>
        <p className="text-muted-foreground max-w-2xl mx-auto">
          Here's a summary of your preferences. You can change these anytime in settings.
        </p>
      </div>

      {/* Summary Cards */}
      <div className="max-w-2xl mx-auto space-y-4">
        {/* User Type */}
        <div className="bg-card border border-border rounded-lg p-6">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
              <Icon name="User" size={24} className="text-primary" />
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-foreground mb-1">User Type</h3>
              <p className="text-sm text-muted-foreground">{getUserTypeLabel(data?.userType)}</p>
            </div>
          </div>
        </div>

        {/* Notifications */}
        <div className="bg-card border border-border rounded-lg p-6">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
              <Icon name="Bell" size={24} className="text-primary" />
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-foreground mb-2">Notifications</h3>
              <div className="space-y-1 text-sm text-muted-foreground">
                <div className="flex items-center gap-2">
                  <Icon
                    name={data?.notifications?.dailyReminders ? 'CheckCircle2' : 'Circle'}
                    size={16}
                    className={data?.notifications?.dailyReminders ? 'text-success' : 'text-muted-foreground'}
                  />
                  <span>Daily reminders {data?.notifications?.dailyReminders ? `at ${data?.notifications?.reminderTime}` : '(disabled)'}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Icon
                    name={data?.notifications?.emailNotifications ? 'CheckCircle2' : 'Circle'}
                    size={16}
                    className={data?.notifications?.emailNotifications ? 'text-success' : 'text-muted-foreground'}
                  />
                  <span>Email notifications</span>
                </div>
                <div className="flex items-center gap-2">
                  <Icon
                    name={data?.notifications?.pushNotifications ? 'CheckCircle2' : 'Circle'}
                    size={16}
                    className={data?.notifications?.pushNotifications ? 'text-success' : 'text-muted-foreground'}
                  />
                  <span>Push notifications</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Privacy */}
        <div className="bg-card border border-border rounded-lg p-6">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
              <Icon name="Shield" size={24} className="text-primary" />
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-foreground mb-2">Privacy Settings</h3>
              <div className="space-y-1 text-sm text-muted-foreground">
                <div className="flex items-center gap-2">
                  <Icon
                    name={data?.privacy?.shareWithCaregivers ? 'CheckCircle2' : 'Circle'}
                    size={16}
                    className={data?.privacy?.shareWithCaregivers ? 'text-success' : 'text-muted-foreground'}
                  />
                  <span>Caregiver sharing</span>
                </div>
                <div className="flex items-center gap-2">
                  <Icon
                    name={data?.privacy?.anonymousAnalytics ? 'CheckCircle2' : 'Circle'}
                    size={16}
                    className={data?.privacy?.anonymousAnalytics ? 'text-success' : 'text-muted-foreground'}
                  />
                  <span>Anonymous analytics</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Next Steps */}
      <div className="max-w-2xl mx-auto">
        <div className="bg-primary/5 border border-primary/20 rounded-lg p-6">
          <h3 className="font-semibold text-foreground mb-3 flex items-center gap-2">
            <Icon name="Sparkles" size={20} className="text-primary" />
            What's Next?
          </h3>
          <ul className="space-y-2 text-sm text-muted-foreground">
            <li className="flex items-start gap-2">
              <Icon name="ArrowRight" size={16} className="text-primary mt-0.5 flex-shrink-0" />
              <span>Complete your first daily check-in</span>
            </li>
            <li className="flex items-start gap-2">
              <Icon name="ArrowRight" size={16} className="text-primary mt-0.5 flex-shrink-0" />
              <span>Explore the symptom history timeline</span>
            </li>
            <li className="flex items-start gap-2">
              <Icon name="ArrowRight" size={16} className="text-primary mt-0.5 flex-shrink-0" />
              <span>Set up caregiver connections if needed</span>
            </li>
          </ul>
        </div>
      </div>

      {/* Navigation */}
      <div className="flex flex-col sm:flex-row gap-3 justify-center max-w-md mx-auto">
        <Button
          variant="outline"
          onClick={onBack}
          disabled={loading}
          iconName="ArrowLeft"
          className="sm:flex-1"
        >
          Back
        </Button>
        <Button
          onClick={onComplete}
          loading={loading}
          iconName="CheckCircle2"
          iconPosition="right"
          className="sm:flex-1"
        >
          Start Using MyBodyToday
        </Button>
      </div>
    </div>
  );
};

export default CompletionStep;